<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="75%">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy"
                @submit.prevent="storeAlertConfig">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>New Device Config</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['name']" :rules="[vRules.required]" density="compact"
                                          autofocus label="Name" placeholder="New Configuration Name"/>
                        </v-col>
                        <v-col v-if="user.type === 'admin'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']" :items="companies" :rules="[vRules.required]"
                                      density="compact" label="Company"
                                      @update:modelValue="()=>{ resetForm(); fetchAlertTemplates('general');
                                          fetchTimeSchedules(); fetchNotificationGroups();
                                      }">
                            </company-select>
                        </v-col>
                        <v-col class="py-0 append-btn" cols="12" sm="6">
                            <v-select v-model="formData['monitoring_notification_group_id']" :items="notificationGroups"
                                      density="compact" item-title="label" item-value="id"
                                      label="Notification Recipients">
                                <template v-slot:append>
                                    <v-icon class="cursor-pointer" icon="mdi-plus" size="small"
                                            :disabled="!formData['company_id']"
                                            @click="recipientsDialog = true"/>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col v-if="user.type === 'admin'" class="pa-0" cols="12" sm="6">
                            <v-checkbox v-model="formData['override_general_config']" label="Override General Config"/>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="py-0 my-3" cols="12">
                            <h4>Configs</h4>
                            <v-table v-if="formData['company_id']">
                                <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Profile</th>
                                    <th>Schedule</th>
                                    <th style="width: 15px">
                                        <v-btn icon="mdi-plus" size="small" variant="flat" @click="addTemplate"/>
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(alert,index) in formData['templates']" :key="index">
                                    <td class="pa-0">
                                        <v-select v-model="alert['alert_type']"
                                                  :items="alertTypes"
                                                  class="text-capitalize" density="compact" label="Type"
                                                  @update:model-value="fetchAlertTemplates(alert['alert_type'],false,index)"
                                        >
                                        </v-select>
                                    </td>
                                    <td class="pa-0 append-btn">
                                        <v-select v-model="alert['alert_template']"
                                                  :items="alertTemplates[alert['alert_type']]"
                                                  :rules="[vRules.required]" density="compact" item-title="label"
                                                  item-value="id"
                                                  label="Profile">
                                            <template v-slot:append>
                                                <v-icon v-if="!(alert.alert_type === 'general' && !user?.super)"
                                                        :disabled="!alert['alert_type'] || alert['alert_type'] === 'system_alert'"
                                                        class="cursor-pointer"
                                                        icon="mdi-plus"
                                                        size="small"
                                                        @click="()=>{
                                                            selectedAlertType=alert['alert_type'];
                                                            selectedRowIndex=index;
                                                            templateDialog = true;
                                                        }"/>
                                            </template>
                                        </v-select>
                                    </td>
                                    <td class="pa-0 append-btn">
                                        <v-select v-model="alert['time_schedule']" :items="timeSchedules"
                                                  :readonly="alert['alert_type'] === 'system_alert'"
                                                  density="compact" item-title="label"
                                                  item-value="id"
                                                  label="Schedule">
                                            <template v-slot:append>
                                                <v-icon class="cursor-pointer" icon="mdi-plus" size="small"
                                                        :disabled="alert['alert_type'] === 'system_alert'"
                                                        @click="()=>{
                                                            selectedRowIndex=index;
                                                            scheduleDialog = true;
                                                        }"/>
                                            </template>
                                        </v-select>
                                    </td>
                                    <td class="pa-0 text-center">
                                        <v-btn
                                            v-if="alert['alert_type']!=='general' || !formData['override_general_config']"
                                            class="mb-3" icon="mdi-close" size="small"
                                            variant="text" @click="removeTemplate(index)"/>
                                    </td>
                                </tr>

                                </tbody>
                            </v-table>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>

    <Create-Schedule v-if="scheduleDialog" v-model="scheduleDialog" :company-id="formData['company_id']"
                     @addSchedule="(newSchedule)=>{
                         setTemplateValue('time_schedule',newSchedule.id);
                         fetchTimeSchedules(false);
                     }"/>

    <Create-Template v-if="templateDialog" v-model="templateDialog" :company-id="formData['company_id']"
                     :type="selectedAlertType ? selectedAlertType.toLowerCase().replace(' ','_') : null"
                     @addTemplate="(newTemplate)=>{
                         setTemplateValue('alert_template',newTemplate.id);
                         fetchAlertTemplates(selectedAlertType,true)
                     }"/>

    <Recipients v-if="recipientsDialog" v-model="recipientsDialog" :company="formData['company_id']"
                @addGroup="(newNotificationGroup)=>{
                    setTemplateValue('notification_group',newNotificationGroup.id);
                    fetchNotificationGroups(false);
                }"/>
</template>

<script setup>
import {ref, reactive, onMounted, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import CreateSchedule from "@/components/dialogs/monitoring/schedules/Create.vue";
import CreateTemplate from "@/components/dialogs/monitoring/templates/Create.vue";
import Recipients from "@/components/dialogs/monitoring/notifications/Recipients.vue";
import CompanySelect from "@/components/globals/company-select.vue";

const props = defineProps({
    modelValue: Boolean,
});
const formReady = ref(false);
const errors = ref([]);
const {user} = useAuthStore();
const companies = ref([]);
const timeSchedules = ref([]);
const alertTemplates = ref({
    'general': [],
    'speed': [],
    'geofence': [],
    'light': [],
    'temperature': [],
    'push_button': [],
    'ble': [],
    'wifi': [],
    'io': [],
    'system_alert': [],
});
const notificationGroups = ref([]);
const scheduleDialog = ref(false);
const templateDialog = ref(false);
const recipientsDialog = ref(false);
const selectedRowIndex = ref(null);
const selectedAlertType = ref(null);
const {showSnackbar} = useSnackbarStore();
const dialogSwitch = ref(props.modelValue);
const formData = reactive({
    valid: false,
    name: null,
    templates: [
        {
            alert_type: 'geofence',
            alert_template: null,
            time_schedule: null,
        },
        {
            alert_type: 'geofence',
            alert_template: null,
            time_schedule: null,
        },
        {
            alert_type: 'speed',
            alert_template: null,
            time_schedule: null,
        }
    ],
    override_general_config: false,
    monitoring_notification_group_id: null,
    company_id: ref((user.type !== 'admin') ? user.company.id : null),
});
const defaultTimeSchedule = ref(null);
const defaultNotificationGroup = ref(null);
const emits = defineEmits(['update:modelValue', 'addConfig']);

onMounted(async () => {
    const promises = [fetchAlertTemplates('general'), fetchAlertTemplates('geofence'), fetchAlertTemplates('speed')];
    if (user.type === 'admin') {
        promises.push(
            send('get', `companies/list`)
                .then((response) => {
                    companies.value = response.data.data;
                })
                .catch((error) => {
                    showSnackbar('Companies List could not be loaded', 'error')
                })
        );
    } else {
        promises.push(
            fetchTimeSchedules().then((resp) => {
                if (timeSchedules.value.length) {
                    defaultTimeSchedule.value = timeSchedules.value.find((schedule) => schedule.type === 1)
                    if (defaultTimeSchedule.value && formData['templates'].length) {
                        formData['templates'].forEach((template) => {
                            template['time_schedule'] = defaultTimeSchedule.value.id;
                        })
                    }
                }
            })
        );
        promises.push(
            fetchNotificationGroups().then((resp) => {
                if (notificationGroups.value.length) {
                    defaultNotificationGroup.value = notificationGroups.value.find((group) => group.type === 1)
                    if (defaultNotificationGroup.value) {
                        formData['monitoring_notification_group_id'] = defaultNotificationGroup.value.id;
                    }
                }
            })
        );
    }
    Promise.all(promises).finally(() => formReady.value = true);
});

const alertTypes = computed(() => {
    const options = ['speed', 'geofence', 'light', 'temperature', 'ble', 'wifi', 'push_button', 'io', 'system_alert'];
    if (user.type === 'admin') {
        const GC = formData['templates'].find((template) => template['alert_type'] === 'general');
        if (!GC) {
            options.unshift('general')
        }
    }
    return options;
});
const setTemplateValue = (prop = 'alert_template', val) => {
    switch (prop) {
        case 'alert_template':
            formData['templates'][selectedRowIndex.value]['alert_template'] = val;
            break;
        case 'time_schedule':
            formData['templates'][selectedRowIndex.value]['time_schedule'] = val;
            break;
        case 'notification_group':
            formData['monitoring_notification_group_id'] = val;
            break;
    }
}
const resetForm = () => {
    formData['templates'] = [];
    formData['monitoring_notification_group_id'] = null;
    alertTemplates.value = {
        'general': [],
        'speed': [],
        'geofence': [],
        'light': [],
        'temperature': [],
        'push_button': [],
        'ble': [],
        'wifi': [],
        'io': [],
        'system_alert': [],
    };
}
const addTemplate = () => {
    formData['templates'].push({
        alert_type: null,
        alert_template: null,
        time_schedule: defaultTimeSchedule.value?.id ?? null,
    })
}
const removeTemplate = (index) => {
    formData['templates'].splice(index, 1)
}
const fetchTimeSchedules = () => {
    return send('get', `monitoring/time-schedules?`, {list: true, company: formData['company_id']})
        .then((response) => {
            timeSchedules.value = response.data.data;
        })
        .catch(() => {
            showSnackbar('Time Schedules could not be loaded', 'error')
        })
}
const fetchAlertTemplates = (alert_type = null, force = false, index = null) => {
    if (index !== null) {
        formData['templates'][index]['time_schedule'] = null;
        formData['templates'][index]['alert_template'] = null;
    }
    if (alert_type === 'system_alert' && index !== null) {
        const defaultAllTime = timeSchedules.value.find((item) => item.type === 1);
        formData['templates'][index]['time_schedule'] = defaultAllTime?.id ?? null;
    }

    if (!alertTemplates.value[alert_type].length || force) {
        return send('get', `templates/list?${(user.type === 'admin') ? "company=" + formData['company_id'] : ''}`, {type: alert_type})
            .then((response) => {
                alertTemplates.value[alert_type] = response.data.data;
            })
            .catch(() => {
                showSnackbar('Alert Templates could not be loaded', 'error')
            })
    }
    return Promise.resolve();
}
const fetchNotificationGroups = (reset = true) => {
    reset ? formData['monitoring_notification_group_id'] = null : '';
    return send('get', `monitoring/notification-groups?`, {list: true, company: formData['company_id']})
        .then((response) => {
            notificationGroups.value = response.data.data;
        })
        .catch(() => {
            showSnackbar('Monitoring notification groups could not be loaded', 'error')
        })
}
const storeAlertConfig = () => {
    errors.value = [];
    send('post', `monitoring/configs`, formData)
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addConfig', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>

<style>
.append-btn .v-input__append {
    margin: 0;
}
</style>
